import { create } from 'zustand'
import { collection, updateDoc, onSnapshot, doc, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'

export interface TrainingSubscribeOptions {
  includeHidden?: boolean
  adminSessionIds?: string[]
}

export interface TrainingState {
  list: any[]
  loading: boolean
  // derived
  listSorted: any[]
  // actions
  subscribeAll: (opts?: TrainingSubscribeOptions) => void
  stopList: () => void
  createTrainingSession: () => Promise<{ success: boolean, id?: string, error?: string }>
  updateTrainingSession: (trainingSessionId: string, data: any) => Promise<{ success: boolean }>
}

// Module-level listener bookkeeping (was closure state in the Pinia setup store).
let listUnsubs: Array<() => void> = []
let sourceMaps: Array<Map<string, any>> = []
let activeSubscribeKey: string | null = null

export const useTrainingStore = create<TrainingState>()(
  derived<TrainingState>((s) => ({
    listSorted: [...s.list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }))((set, get) => {
    function emitMergedList () {
      const merged = new Map()
      for (const sourceMap of sourceMaps) {
        for (const [id, row] of sourceMap) merged.set(id, row)
      }
      set({ list: [...merged.values()], loading: false })
    }

    function attachQuerySource (q, label) {
      const sourceMap = new Map()
      sourceMaps.push(sourceMap)
      const unsub = onSnapshot(
        q,
        (snap: any) => {
          sourceMap.clear()
          for (const d of snap.docs) {
            sourceMap.set(d.id, { id: d.id, ...d.data() })
          }
          emitMergedList()
        },
        (err) => {
          console.error(`Training list (${label}):`, err)
          set({ loading: false })
        }
      )
      listUnsubs.push(() => {
        unsub()
        sourceMaps = sourceMaps.filter((m) => m !== sourceMap)
        emitMergedList()
      })
    }

    function attachDocSource (trainingSessionId) {
      if (!trainingSessionId) return
      const sourceMap = new Map()
      sourceMaps.push(sourceMap)
      const unsub = onSnapshot(
        doc(db, 'trainingSessions', trainingSessionId),
        (snap) => {
          sourceMap.clear()
          if (snap.exists()) {
            sourceMap.set(snap.id, { id: snap.id, ...snap.data() })
          }
          emitMergedList()
        },
        (err) => console.error(`Training doc (${trainingSessionId}):`, err)
      )
      listUnsubs.push(() => {
        unsub()
        sourceMaps = sourceMaps.filter((m) => m !== sourceMap)
        emitMergedList()
      })
    }

    /**
     * Public/normal users query visible clinics only.
     * An unfiltered collection listen is denied by rules for anyone who is not
     * an admin, which previously left the hub empty for players.
     * Super admins can query all sessions, including hidden drafts.
     * Training admins/captains additionally subscribe to each assigned id.
     */
    const subscribeAll = (opts: TrainingSubscribeOptions = {}) => {
      const includeHidden = opts.includeHidden === true
      const adminSessionIds = Array.isArray(opts.adminSessionIds)
        ? [...new Set(opts.adminSessionIds.filter(Boolean))]
        : []
      const nextKey = JSON.stringify({
        includeHidden,
        adminSessionIds: [...adminSessionIds].sort()
      })

      if (activeSubscribeKey === nextKey && listUnsubs.length > 0) return
      stopList()
      activeSubscribeKey = nextKey
      set({ loading: true })

      const col = collection(db, 'trainingSessions')
      if (includeHidden) {
        attachQuerySource(col, 'all')
        return
      }

      if (import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true') {
        attachQuerySource(col, 'emulator-full')
        return
      }

      attachQuerySource(query(col, where('isVisible', '==', true)), 'visible-true')
      attachQuerySource(query(col, where('isVisible', '==', null)), 'visible-null')

      for (const id of adminSessionIds) {
        attachDocSource(id)
      }
    }

    const stopList = () => {
      for (const unsub of listUnsubs) unsub()
      listUnsubs = []
      sourceMaps = []
      activeSubscribeKey = null
      set({ list: [] })
    }

    return {
      list: [],
      loading: false,
      listSorted: [],

      subscribeAll,
      stopList,

      createTrainingSession: async () => {
        try {
          if (!auth.currentUser?.uid) return { success: false, error: 'Not signed in' }
          const fn = httpsCallable(functions, 'adminCreateTrainingSessionDraft')
          const result: any = await fn({})
          const d = result.data || {}
          const id = d.trainingSessionId || d.id
          if (!id) return { success: false, error: 'No training session id returned.' }
          return { success: true, id }
        } catch (e) {
          console.error('createTrainingSession:', e)
          return {
            success: false,
            error: e?.message || (typeof e === 'string' ? e : 'Could not create training session.')
          }
        }
      },

      updateTrainingSession: async (trainingSessionId, data) => {
        const dref = doc(db, 'trainingSessions', trainingSessionId)
        await updateDoc(dref, {
          ...data,
          updatedAt: new Date().toISOString()
        })
        return { success: true }
      }
    }
  })
)
